import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';

import { useNavigateReplace } from 'hooks';
import { useGetEstablishmentsQuery, useInfoUserQuery } from 'store/api';
import { useGetRegionsQuery, useUpdateUserMutation } from 'store/api/private';
import { useAppDispatch } from 'store/hooks/use-app';
import { logout } from 'store/slices/user-slice';
import { routes } from 'routes';

import type { UserInfo } from '../utils';
import { useUserInfoValidation } from '../utils';

export const useProfileForm = () => {
	const navigate = useNavigateReplace();
	const dispatch = useAppDispatch();
	// const [isLoading, setIsLoading] = useState(true);
	const { data: userData, error, isLoading, isError, refetch } = useInfoUserQuery(null);
	const { data: regions } = useGetRegionsQuery();
	const { data: response } = useGetEstablishmentsQuery(
		{ pageNumber: 0, pageSize: 100, sort: 'DESC', sortField: 'id' } ?? skipToken,
	);
	const [updateUser] = useUpdateUserMutation();

	const [hasTriggeredUncheck, setHasTriggeredUncheck] = useState(false);

	const data = useMemo(() => {
		return userData;
	}, [userData]);

	const establishments = response?.content;

	const { userInfoSchema } = useUserInfoValidation();

	const methods = useForm<UserInfo>({
		resolver: zodResolver(userInfoSchema),
		mode: 'onChange',
		defaultValues: {
			email: '',
			lastName: '',
			firstName: '',
			middleName: '',
			region: '',
			establishment: '',
			specialty: '',
			userAgreement: false,
			personalDataAgreement: false,
		},
	});
	useEffect(() => {
		console.log(data);
	}, [data]);
	const {
		watch,
		setValue,
		formState: { errors, isSubmitted, isValid },
	} = methods;

	const formValues = watch();

	useEffect(() => {
		if (data) {
			setValue('email', data.email || '');
			setValue('lastName', data.lastName || '');
			setValue('firstName', data.firstName || '');
			setValue('middleName', data.middleName || '');
			setValue('region', data.region?.fullname || '');
			setValue('establishment', data.establishment?.name || '');
			setValue('specialty', data.specialty || '');
			setValue('userAgreement', data.userAgreement || false);
			setValue('personalDataAgreement', data.personalDataAgreement || false);
		}
	}, [data, setValue]);

	const isDataChanged = useMemo(() => {
		if (!userData) return false;

		return (
			formValues.lastName !== userData.lastName ||
			formValues.firstName !== userData.firstName ||
			formValues.middleName !== userData.middleName ||
			formValues.region !== userData.region?.fullname ||
			formValues.establishment !== userData.establishment?.name ||
			formValues.specialty !== userData.specialty
		);
	}, [formValues, userData]);

	useEffect(() => {
		if (!userData) return;

		if (isDataChanged && !hasTriggeredUncheck) {
			if (formValues.userAgreement && formValues.personalDataAgreement) {
				setValue('userAgreement', false);
				setValue('personalDataAgreement', false);
			}

			setHasTriggeredUncheck(true);
		} else if (!isDataChanged && hasTriggeredUncheck) {
			setHasTriggeredUncheck(false);
		}
	}, [
		isDataChanged,
		hasTriggeredUncheck,
		formValues.userAgreement,
		formValues.personalDataAgreement,
		setValue,
		userData,
	]);

	useEffect(() => {
		// const timer = setTimeout(() => {
		// 	setIsLoading(apiIsLoading);
		// }, 2000);

		if (isError) {
			toast.error((error as any).data.message, {
				position: 'top-right',
			});
			dispatch(logout());
			navigate(routes.login.path);
		}

		// return () => clearTimeout(timer);
	}, [isLoading, isError, dispatch, navigate, error]);

	const onSubmit: SubmitHandler<UserInfo> = async (values) => {
		try {
			await updateUser({
				...values,
				establishment: values.establishment ? { name: values.establishment } : null,
				region: { fullname: values.region },
			});
			console.log(values);
			toast.success('Данные успешно сохранены');
			await refetch();
			navigate(routes.root.path);
		} catch (error) {
			toast.error('Ошибка при сохранении данных');
		}
	};

	return {
		userData,
		methods,
		isLoading,
		error,
		regions,
		establishments,
		handleSubmit: methods.handleSubmit(onSubmit),
		errors,
		isSubmitted,
		isValid,
		formValues,
		setValue,
	};
};
